import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { register } = useAuth();
  const navigate = useNavigate();


  // Function to validate form
  const validateForm = () => {
    const newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('auth.validation.firstNameRequired', 'First name is required');
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = t('auth.validation.firstNameMinLength', 'First name must be at least 2 characters');
    }

    // Last Name validation (optional)
    if (formData.lastName.trim() && formData.lastName.trim().length < 2) {
      newErrors.lastName = t('auth.validation.lastNameMinLength', 'Last name must be at least 2 characters');
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = t('auth.validation.emailRequired', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth.validation.emailInvalid', 'Please enter a valid email address');
    }


    // Password validation
    if (!formData.password) {
      newErrors.password = t('auth.validation.passwordRequired', 'Password is required');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.validation.passwordMinLength', 'Password must be at least 8 characters long');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      newErrors.password = t('auth.validation.passwordComplexity', 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.validation.confirmPasswordRequired', 'Please confirm your password');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.validation.passwordMismatch', 'Passwords do not match');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Use email as username for registration
      const registrationData = {
        ...formData,
        username: formData.email
      };
      
      const result = await register(registrationData);
      
      if (result.success) {
        // Show success message and redirect to login instead of dashboard
        setErrors({ 
          success: t('auth.registrationSuccess', 'Registration successful! Please check your email for confirmation link.') 
        });
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Handle registration errors
        if (result.error) {
          setErrors({ general: result.error });
        }
      }
    } catch (error) {
      setErrors({ general: t('auth.registrationFailed', 'Registration failed. Please try again.') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-900 p-8 rounded-xl shadow-lg w-full max-w-lg border border-gray-700">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-extrabold text-white">{t('auth.createAccount')}</h2>
        <p className="mt-2 text-sm text-gray-400">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">
            {t('auth.login')}
          </Link>
        </p>
      </div>

      {/* General error message */}
      {errors.general && (
        <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-md">
          <p className="text-red-400 text-sm">{errors.general}</p>
        </div>
      )}

      {/* Success message */}
      {errors.success && (
        <div className="mb-6 p-4 bg-green-900/50 border border-green-700 rounded-md">
          <p className="text-green-400 text-sm">{errors.success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1">
              {t('auth.firstName')} *
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-500" />
              </div>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                className={`appearance-none block w-full px-3 py-2 pl-10 border rounded-md placeholder-gray-500 focus:outline-none focus:ring-2 sm:text-sm bg-gray-800 text-white ${
                  errors.firstName 
                    ? 'border-red-600 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-600 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder={t('auth.firstName')}
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1">
              {t('auth.lastName')}
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-500" />
              </div>
              <input
                id="lastName"
                name="lastName"
                type="text"
                className={`appearance-none block w-full px-3 py-2 pl-10 border rounded-md placeholder-gray-500 focus:outline-none focus:ring-2 sm:text-sm bg-gray-800 text-white ${
                  errors.lastName 
                    ? 'border-red-600 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-600 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder={t('auth.lastName')}
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
            {t('auth.email')} *
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-500" />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className={`appearance-none block w-full px-3 py-2 pl-10 border rounded-md placeholder-gray-500 focus:outline-none focus:ring-2 sm:text-sm bg-gray-800 text-white ${
                errors.email 
                  ? 'border-red-600 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-600 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder={t('auth.email')}
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-400">{errors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
            {t('auth.password')} *
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-500" />
            </div>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              className={`appearance-none block w-full px-3 py-2 pl-10 pr-10 border rounded-md placeholder-gray-500 focus:outline-none focus:ring-2 sm:text-sm bg-gray-800 text-white ${
                errors.password 
                  ? 'border-red-600 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-600 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder={t('auth.password')}
              value={formData.password}
              onChange={handleChange}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-300"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-400">{errors.password}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
            {t('auth.confirmPassword')} *
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-500" />
            </div>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              className={`appearance-none block w-full px-3 py-2 pl-10 pr-10 border rounded-md placeholder-gray-500 focus:outline-none focus:ring-2 sm:text-sm bg-gray-800 text-white ${
                errors.confirmPassword 
                  ? 'border-red-600 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-600 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder={t('auth.confirmPassword')}
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-300"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
          )}
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                {t('auth.creatingAccount')}
              </div>
            ) : (
              t('auth.register')
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Register; 